import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import CancelIcon from '@mui/icons-material/Cancel';
import { matchSorter } from 'match-sorter';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import {
  useGetSolutionActivationList,
  useUpdateSolutionActivationStatus,
} from 'services/v1/Tenant/ConfigurationService';
import { SolutionActivationItem } from 'types/api/Tenant/ConfigurationTypes';
import { getErrorMessage } from 'utils/Error';

const textInputStyles = {
  width: '100%',
  maxWidth: 320,
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

export const ComponentsConfigurationPage = () => {
  const [searchKeyword, setSearchKeyword] = useState('');

  const { data: solutionList, isLoading, isError, error } = useGetSolutionActivationList();
  const { mutate: updateActivationStatus } = useUpdateSolutionActivationStatus();

  const solutions = useMemo(() => solutionList?.data ?? [], [solutionList?.data]);

  const filteredSolutions = useMemo(() => {
    const base = searchKeyword
      ? matchSorter(solutions, searchKeyword, {
          keys: ['solutionName', 'solutionDefinitionId'],
          baseSort: () => 0,
        })
      : solutions;
    return [...base].sort((a, b) => a.solutionName.localeCompare(b.solutionName));
  }, [solutions, searchKeyword]);

  const handleToggle = (solution: SolutionActivationItem, enabled: boolean) => {
    updateActivationStatus(
      {
        solutionDefinitionId: solution.solutionDefinitionId,
        enabled,
      },
      {
        onError: (mutationError) => {
          toast.dismiss();
          toast.error(getErrorMessage(mutationError));
        },
      }
    );
  };

  return (
    <Box sx={{ bgcolor: '#fff', px: 1, py: 2, width: '100%' }}>
      <Stack mb={2} width='100%'>
        <Typography variant='h6' color='text.primary' fontWeight='bold'>
          Components
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Control which solution components are available in My View. Changes are saved immediately.
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Stack>
      {isLoading ? (
        <Stack direction='column' justifyContent='center'>
          <ActivityIndicator />
        </Stack>
      ) : isError ? (
        <Typography variant='body2' color='error'>
          {getErrorMessage(error)}
        </Typography>
      ) : (
        <Stack width='100%' maxWidth={640} gap={1}>
          <TextField
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            name='search'
            sx={textInputStyles}
            size='small'
            placeholder='Search solution...'
            type='text'
            InputProps={{
              endAdornment: (
                <CancelIcon
                  sx={{
                    display: searchKeyword ? 'block' : 'none',
                    fontSize: 14,
                    color: '#98A2AE',
                    cursor: 'pointer',
                  }}
                  onClick={() => setSearchKeyword('')}
                />
              ),
            }}
          />
          {filteredSolutions.length === 0 ? (
            <Typography variant='body2' color='text.secondary' mt={2}>
              {solutions.length === 0 ? 'No solutions available.' : 'No solutions match your search.'}
            </Typography>
          ) : (
            <Stack sx={{ border: '1px solid #F0F0F0', borderRadius: 1 }} mt={1}>
              {filteredSolutions.map((solution, index) => (
                <Stack
                  key={solution.solutionDefinitionId}
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  gap={1}
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    borderTop: index === 0 ? 'none' : '1px solid #F0F0F0',
                  }}
                >
                  <Typography
                    fontSize={13}
                    fontWeight={500}
                    color={solution.active ? 'text.primary' : 'text.disabled'}
                    noWrap
                  >
                    {solution.solutionName}
                  </Typography>
                  <Switch
                    size='small'
                    checked={solution.active}
                    onChange={(_event, checked) => handleToggle(solution, checked)}
                    inputProps={{ 'aria-label': `Toggle ${solution.solutionName}` }}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};
